/**
 * Extracts initials (up to 2 characters) from a display name string.
 * Falls back to '?' if the name is empty.
 */
export function getInitials(name: string | undefined | null): string {
  if (!name) return '?';

  const trimmed = name.trim();
  if (!trimmed) return '?';

  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  return trimmed.substring(0, 2).toUpperCase();
}

/**
 * Extracts initials from an email address (first 2 chars of local part).
 * Falls back to '?' if the email is empty.
 */
export function getInitialsFromEmail(email: string | undefined | null): string {
  if (!email) return '?';
  const localPart = email.split('@')[0]?.trim();
  if (!localPart) return '?';
  return localPart.substring(0, 2).toUpperCase();
}

/** Design-system utility color CSS variables used for avatar differentiation. */
export const AVATAR_COLORS = [
  'var(--utility-mintgreen-500)',
  'var(--utility-indigo-500)',
  'var(--utility-lime-500)',
  'var(--utility-tealgreen-500)',
  'var(--utility-purple-500)',
  'var(--utility-skyblue-500)',
  'var(--utility-orange-500)',
  'var(--utility-pink-500)',
  'var(--utility-red-500)',
] as const;

/**
 * Deterministically maps a seed string (entity ID) to one of the avatar color CSS variables.
 * Uses a position-weighted character code sum so that e.g. "AB" and "BA" produce different colors.
 * The same seed always returns the same color across sessions and devices.
 */
export function getAvatarColor(seed: string): string {
  if (!seed) return AVATAR_COLORS[0];
  const hash = seed.split('').reduce((acc, char, i) => acc + char.charCodeAt(0) * (i + 1), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}
